"use client";

import { useCallback, useEffect, useState } from "react";
import { Bug, LayoutGrid, LayoutList, Lightbulb, MessageSquarePlus, Search, Sparkles } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { SegmentedTabSwitcher, type SegmentedTabItem } from "@aera/ui";
import {
  loadErrorToThai,
  SUPPORT_CARD_CLASS,
  SupportEmptyState,
  SupportErrorState,
  SupportLoadingState,
  SupportPageHeader,
  SupportTicketList,
} from "@/app/components/support/supportUi";
import {
  supportApi,
  type SupportTicketCategory,
  type SupportTicketListItem,
} from "@/lib/api";

const FILTERS: SegmentedTabItem<"ALL" | SupportTicketCategory>[] = [
  { id: "ALL", label: "ทั้งหมด", icon: LayoutGrid },
  { id: "ISSUE", label: "ปัญหา", icon: Bug },
  { id: "REQUEST", label: "ขอฟีเจอร์", icon: Sparkles },
  { id: "SUGGESTION", label: "ข้อเสนอแนะ", icon: Lightbulb },
];

export default function SupportBoardPage() {
  const [filter, setFilter] = useState<"ALL" | SupportTicketCategory>("ALL");
  const [search, setSearch] = useState("");
  const [tickets, setTickets] = useState<SupportTicketListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await supportApi.listPublic({
        category: filter === "ALL" ? undefined : filter,
        search: search.trim() || undefined,
        page: 1,
        pageSize: 50,
      });
      setTickets(res.data?.tickets ?? []);
    } catch (e) {
      setError(loadErrorToThai(e instanceof Error ? e.message : ""));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  return (
    <div className="space-y-5">
      <SupportPageHeader
        icon={<LayoutList className="h-5 w-5 text-aera-amber" aria-hidden />}
        title="บอร์ดสาธารณะ"
        description={
          <>
            แจ้งปัญหา ขอฟีเจอร์ และข้อเสนอแนะ ·
            อ่านและตอบกลับได้เมื่อเธรดเปิดอยู่
          </>
        }
      >
        <p className="mt-4 rounded-xl border border-amber-200/80 bg-amber-50/80 px-3 py-2 text-xs leading-relaxed text-amber-950">
          เรื่องการชำระเงินหรือบัญชีที่ละเอียดอ่อน กรุณา{" "}
          <AeraButton
            href="/support/new/issue"
            variant="link"
            fontWeight="medium"
          >
            สร้างตั๋วส่วนตัว
          </AeraButton>{" "}
          และระบุอีเมลที่ผูกกับบัญชี
        </p>
      </SupportPageHeader>

      <div className={`${SUPPORT_CARD_CLASS} space-y-4 p-4 md:p-6`}>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-aera-gray"
            aria-hidden
          />
          <input
            type="search"
            placeholder="ค้นหาหัวข้อหรือรายละเอียด…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-aera-charcoal/15 bg-aera-offwhite py-3 pl-10 pr-4 text-sm text-aera-charcoal placeholder:text-aera-gray outline-none transition focus:border-aera-amber focus:ring-2 focus:ring-aera-amber/20"
          />
        </div>

        <SegmentedTabSwitcher
          tabs={FILTERS}
          activeTab={filter}
          onChange={setFilter}
          ariaLabel="กรองตามประเภท"
        />

        {loading ? (
          <SupportLoadingState />
        ) : error ? (
          <SupportErrorState error={error} onRetry={() => void load()} />
        ) : tickets.length === 0 ? (
          <SupportEmptyState
            icon={
              <MessageSquarePlus
                className="h-6 w-6 text-aera-amber"
                aria-hidden
              />
            }
            title="ยังไม่มีตั๋วสาธารณะ"
            description={
              search.trim() || filter !== "ALL"
                ? "ลองเปลี่ยนคำค้นหาหรือตัวกรอง"
                : "เป็นคนแรกที่แชร์ปัญหาหรือไอเดียกับชุมชน"
            }
          />
        ) : (
          <SupportTicketList tickets={tickets} showAuthor />
        )}
      </div>
    </div>
  );
}
